import type { APIClient } from '@src/shared/api';

import { useMutation, useQuery } from '@src/shared/query';

export type PasswordChangePayload = {
  otp_code: string;
  password: string;
};

type PasswordChangeResponse = {
  detail: string;
  access: string;
  refresh: string;
};

type UserPasswordResetResponse = {
  detail: string;
  access?: string;
  refresh?: string;
};

type UserTwoFactorResetPayload = {
  id: number | string;
};

type UserTwoFactorResetResponse = {
  detail: string;
};

export type UserTwoFactorConfiguration = {
  challenge_token: string | null;
  enabled: boolean;
  manual_entry_key: string;
  qr_code_data_url: string;
};

type UserTwoFactorEnrollmentPayload = {
  challenge_token: string;
  id: number | string;
  otp_code: string;
};

export function usePasswordChange() {
  return useMutation<PasswordChangeResponse, Error, PasswordChangePayload>({
    mutationKey: ['auth', 'password-change'],
    mutateFn: ({
      apiClient,
      ...payload
    }: { apiClient: APIClient } & Record<string, unknown>) => {
      return apiClient.request({
        url: `${import.meta.env.VITE_AUTH_DOMAIN}/api/v1/auth/password/change/`,
        method: 'POST',
        payload,
      }) as Promise<PasswordChangeResponse>;
    },
  });
}

type UserPasswordResetPayload = {
  id: number | string;
  otp_code: string;
  password: string;
};

export function useUserPasswordReset() {
  return useMutation<
    UserPasswordResetResponse,
    Error,
    UserPasswordResetPayload
  >({
    mutationKey: ['users', 'password-reset'],
    mutateFn: ({ apiClient, id, otp_code, password }) => {
      return apiClient.request({
        url: `users/${String(id)}/password/reset/`,
        method: 'POST',
        payload: { otp_code, password },
      }) as Promise<UserPasswordResetResponse>;
    },
  });
}

export function useUserTwoFactorReset() {
  return useMutation<
    UserTwoFactorResetResponse,
    Error,
    UserTwoFactorResetPayload
  >({
    mutationKey: ['users', 'two-factor-reset'],
    mutateFn: ({ apiClient, id }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/reset/`,
        method: 'POST',
      }) as Promise<UserTwoFactorResetResponse>;
    },
  });
}

export function useUserTwoFactorConfiguration(
  id: number | string,
  enabled: boolean,
) {
  return useQuery<UserTwoFactorConfiguration>(
    `users/${String(id)}/two-factor/`,
    { enabled },
  );
}

export function useUserTwoFactorEnrollment() {
  return useMutation<
    UserTwoFactorConfiguration,
    Error,
    UserTwoFactorEnrollmentPayload
  >({
    mutationKey: ['users', 'two-factor-enrollment'],
    mutateFn: ({ apiClient, id, challenge_token, otp_code }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/confirm/`,
        method: 'POST',
        payload: { challenge_token, otp_code },
      }) as Promise<UserTwoFactorConfiguration>;
    },
  });
}
